import React, { useEffect, useState } from "react";
import { Button } from "@mui/material";
import { makeStyles } from "@Compat/makeStyles";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarCheck } from '@fortawesome/free-solid-svg-icons/faCalendarCheck';
import { useSiteContent } from "../SiteContentContext";
import { recordCta } from "../marketing";
import { isCalendlyHref, useDemoDrawer } from "../DemoDrawerContext";
import { siteTokens } from "../siteTokens";

const useStyles = makeStyles((theme) => {
  const tk = siteTokens(theme);
  return {
  // Fixed-bottom bar — only visible on small viewports (md down) and
  // only after the visitor has scrolled past the hero. Keeps the
  // primary CTA reachable when the hero CTA is off-screen, which is
  // the single biggest mobile conversion lever.
  shell: {
    position: "fixed",
    left: 0,
    right: 0,
    // Sits above the cookie banner instead of underneath it when both are
    // visible at once (visitor scrolled past the hero before deciding on
    // cookies) — same `--consent-banner-offset` custom property the
    // banner's height sets on VerticalScroller's container, which this
    // element inherits since it's rendered inside that same container.
    // Pinned to the very bottom; the cookie-banner offset is applied as a
    // TRANSFORM (shellVisible) rather than through `bottom`. Changing
    // `bottom` when the banner appears is a layout change, and PageSpeed
    // counted it as most of the page's mobile layout shift (0.118).
    bottom: 0,
    zIndex: 1200,
    padding: theme.spacing(1.5, 2),
    background: tk.surfaceA(tk.dark ? 0.94 : 0.92),
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    boxShadow: theme.palette.mode === "dark"
      ? "0 -8px 24px rgba(0,0,0,0.4)"
      : `0 -8px 24px ${tk.accentDeepA(0.12)}`,
    borderTop: `1px solid ${tk.border}`,
    transform: "translateY(110%)",
    // Hidden (not just off-screen) while parked, so nothing about it counts
    // as visible movement; visibility flips after the slide-out finishes.
    visibility: "hidden",
    transition: "transform 240ms ease, visibility 0s linear 240ms",
    [theme.breakpoints.up("md")]: {
      display: "none"
    }
  },
  shellVisible: {
    transform: "translateY(calc(-1 * var(--consent-banner-offset, 0px)))",
    visibility: "visible",
    transition: "transform 240ms ease, visibility 0s linear 0s"
  },
  cta: {
    "&&": {
      width: "100%",
      padding: theme.spacing(1.4, 3),
      borderRadius: 999,
      fontWeight: 700,
      fontSize: "1rem",
      textTransform: "none",
      color: tk.onAccent,
      background: tk.gradient(135),
      boxShadow: `0 14px 28px ${tk.accentDeepA(0.36)}`
    }
  }
  };
});

const MobileStickyCta: React.FC = () => {
  const classes = useStyles();
  const { content, locale } = useSiteContent();
  const { open: openDemoDrawer } = useDemoDrawer();
  const [visible, setVisible] = useState(false);

  const cta = content.hero?.primaryCta;
  const href = cta?.href ?? "https://calendly.com/vasco-galvao/nova-reuniao";
  const label = cta?.label ?? "Book a demo";

  useEffect(() => {
    // Visible once the visitor has scrolled past the hero (the easiest
    // proxy: 60% of viewport height). Listens on both window and on
    // VerticalScroller's container, which is where the site scrolls —
    // window scroll alone misses it.
    const threshold = () => Math.max(420, Math.floor(window.innerHeight * 0.6));
    const measure = () => {
      const inner = document.querySelector<HTMLElement>("[data-site-scroller]");
      const innerTop = inner?.scrollTop ?? 0;
      const windowTop = window.scrollY || document.documentElement.scrollTop || 0;
      const top = Math.max(innerTop, windowTop);
      setVisible(top > threshold());
    };
    measure();
    window.addEventListener("scroll", measure, { passive: true });
    const inner = document.querySelector<HTMLElement>("[data-site-scroller]");
    inner?.addEventListener("scroll", measure, { passive: true });
    return () => {
      window.removeEventListener("scroll", measure);
      inner?.removeEventListener("scroll", measure);
    };
  }, []);

  if (!cta) return null;

  return (
    <div className={`${classes.shell}${visible ? ` ${classes.shellVisible}` : ""}`} role="region" aria-label="Book a demo">
      <Button
        component="a"
        href={href}
        target={href.startsWith("http") && !isCalendlyHref(href) ? "_blank" : undefined}
        rel={href.startsWith("http") && !isCalendlyHref(href) ? "noopener noreferrer" : undefined}
        className={classes.cta}
        variant="contained"
        startIcon={<FontAwesomeIcon icon={faCalendarCheck} />}
        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
          recordCta("mobile-sticky-cta", { locale, href });
          if (isCalendlyHref(href)) {
            e.preventDefault();
            openDemoDrawer(href);
          }
        }}
      >
        {label}
      </Button>
    </div>
  );
};

export default MobileStickyCta;
